import type { PersonProfilePayload, ProfileRelationship, ProfileSeason } from './profileTypes'

export type CatalogConnection = {
  name: string
  personId?: string
  relationshipId?: string
  context: string
}

export type CatalogSeasonChapter = {
  show: string
  season: number
  title: string
  recap: string
  status?: 'aired' | 'announced'
  partnerPersonIds?: string[]
  relationshipId?: string
}

export type CatalogProfilePresentation = {
  shortName: string
  headline: string
  bio: string[]
  originStory: string
  connections: CatalogConnection[]
  seasons: CatalogSeasonChapter[]
}

function naturalList(values: string[]) {
  if (values.length === 0) return ''
  if (values.length === 1) return values[0]
  if (values.length === 2) return `${values[0]} and ${values[1]}`
  return `${values.slice(0, -1).join(', ')}, and ${values.at(-1)}`
}

function plural(value: number, singular: string, pluralForm = `${singular}s`) {
  return value === 1 ? singular : pluralForm
}

export function shortCastName(name: string) {
  const quoted = name.match(/["“]([^"”]+)["”]/)?.[1]?.trim()
  if (quoted) return quoted
  if (/\bbig ed\b/i.test(name)) return 'Big Ed'
  return name.trim().split(/\s+/)[0] || 'This cast member'
}

function sameSeason(left: { show: string; season: number }, right: { show: string; season: number }) {
  return left.season === right.season && left.show.localeCompare(right.show, undefined, { sensitivity: 'base' }) === 0
}

function otherMembers(relationship: ProfileRelationship, personId: string) {
  return relationship.members.filter((member) => member.person_id !== personId)
}

function relationshipsForSeason(record: PersonProfilePayload, season: ProfileSeason) {
  return record.relationships.filter((relationship) => relationship.seasons.some((appearance) => sameSeason(appearance, season)))
}

function connectionContext(relationship: ProfileRelationship) {
  const opening = relationship.first_show
    ? `${relationship.first_show}${relationship.first_season ? ` Season ${relationship.first_season}` : ''}`
    : 'the franchise catalog'
  const seasonCount = new Set(relationship.seasons.map((season) => `${season.show.toLocaleLowerCase()}::${season.season}`)).size
  return `This shared story begins in ${opening} and connects to ${seasonCount || 1} indexed ${plural(seasonCount || 1, 'season')}. Open the couple journey for the full route.`
}

function generatedConnections(record: PersonProfilePayload): CatalogConnection[] {
  const connections: CatalogConnection[] = []
  const seen = new Set<string>()

  for (const relationship of record.relationships) {
    for (const member of otherMembers(relationship, record.person.id)) {
      const key = `${relationship.id}::${member.person_id}`
      if (seen.has(key)) continue
      seen.add(key)
      connections.push({
        name: member.name,
        personId: member.person_id,
        relationshipId: relationship.id,
        context: connectionContext(relationship),
      })
    }
  }

  if (connections.length === 0 && record.person.first_partner) {
    connections.push({
      name: record.person.first_partner,
      context: `The catalog names ${record.person.first_partner} in ${shortCastName(record.person.name)}’s opening franchise chapter. A direct profile link will appear once that identity is matched to the cast index.`,
    })
  }

  return connections
}

function seasonChapter(record: PersonProfilePayload, season: ProfileSeason): CatalogSeasonChapter {
  const shortName = shortCastName(record.person.name)
  const relationships = relationshipsForSeason(record, season)
  const partners = relationships.flatMap((relationship) => otherMembers(relationship, record.person.id))
  const uniquePartners = [...new Map(partners.map((partner) => [partner.person_id, partner])).values()]
  const partnerNames = uniquePartners.map((partner) => partner.name)
  const isOpeningSeason = record.person.first_show?.localeCompare(season.show, undefined, { sensitivity: 'base' }) === 0
    && record.person.first_season === season.season
  const episodeCount = season.episodes.length

  let title = `${shortName} joins the season`
  let recap = `${season.show} Season ${season.season} adds another chapter to ${shortName}’s franchise story. The episode guide below connects ${episodeCount || 'the indexed'} ${plural(episodeCount, 'episode')} without requiring a corkboard, red string, or fourteen open tabs.`

  if (partnerNames.length > 0) {
    title = isOpeningSeason ? `The opening chapter with ${naturalList(partnerNames)}` : `Another chapter with ${naturalList(partnerNames)}`
    recap = `${isOpeningSeason ? 'This is the first indexed season for' : 'This season continues'} ${shortName} and ${naturalList(partnerNames)} on ${season.show}. Journey90 connects the relationship record with ${episodeCount || 'the season’s'} ${plural(episodeCount, 'episode')} below, so the storyline stays easier to follow than the group chat.`
  } else if (/hunt for love|single life/i.test(season.show)) {
    title = 'A solo chapter enters the itinerary'
    recap = `${shortName} returns for an individual dating chapter on ${season.show} Season ${season.season}. The guide below collects ${episodeCount || 'the indexed'} ${plural(episodeCount, 'episode')} in one place—because finding love is hard enough without losing the episode list.`
  }

  return {
    show: season.show,
    season: season.season,
    title,
    recap,
    partnerPersonIds: uniquePartners.map((partner) => partner.person_id),
    relationshipId: relationships[0]?.id,
  }
}

export function buildCatalogProfilePresentation(record: PersonProfilePayload | null, fallbackName: string): CatalogProfilePresentation {
  const name = record?.person.name || fallbackName
  const shortName = shortCastName(name)

  if (!record) {
    return {
      shortName,
      headline: `${shortName}’s franchise route is being connected to the Journey90 episode index.`,
      bio: ['The cast record is loading its relationships, seasons and episode guide. Even reality television occasionally needs a commercial break.'],
      originStory: 'The opening chapter will appear as soon as the catalog record finishes loading.',
      connections: [],
      seasons: [],
    }
  }

  const showNames = [...new Set(record.seasons.map((season) => season.show))]
  const seasonCount = record.seasons.length
  const connectionNames = generatedConnections(record).map((connection) => connection.name)
  const openingShow = record.person.first_show
    ? `${record.person.first_show}${record.person.first_season ? ` Season ${record.person.first_season}` : ''}`
    : record.seasons[0]
      ? `${record.seasons[0].show} Season ${record.seasons[0].season}`
      : 'the 90 Day universe'
  const openingPartner = record.person.first_partner || connectionNames[0]
  const billedFrom = record.person.billed_from ? `, billed from ${record.person.billed_from},` : ''

  const headline = seasonCount > 0
    ? `From ${openingShow} to ${seasonCount} credited ${plural(seasonCount, 'season')}, ${shortName} has a franchise passport with very little blank space.`
    : `${shortName} has an indexed place in the 90 Day universe, with the next chapter waiting to be connected.`

  const firstBio = `${name}${billedFrom} entered the franchise through ${openingShow}${openingPartner ? ` alongside ${openingPartner}` : ''}. Journey90 now tracks ${seasonCount || 'the available'} credited ${plural(seasonCount, 'season')} across ${showNames.length || 1} ${plural(showNames.length || 1, 'show')}—enough television to make “just one more episode” a highly suspicious promise.`
  const secondBio = connectionNames.length > 0
    ? `The indexed story connects ${shortName} with ${naturalList(connectionNames)}. This page keeps the people, relationship chapters and episode guides together, because franchise history should feel like a good recap—not a scavenger hunt.`
    : `This page keeps ${shortName}’s season trail and episode guides together while the relationship index continues to grow. Consider it the organized friend who remembers which spinoff came next.`

  const originStory = `${shortName}’s first indexed chapter begins on ${openingShow}${openingPartner ? ` with ${openingPartner}` : ''}. From there, the timeline follows every credited season in the Journey90 catalog and links each available relationship back to the cast members involved.`

  return {
    shortName,
    headline,
    bio: [firstBio, secondBio],
    originStory,
    connections: generatedConnections(record),
    seasons: record.seasons.map((season) => seasonChapter(record, season)),
  }
}
